import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../data/site";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[#DCE7F5]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="aqua-container flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: "#1E7BE0" }}
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <path
                d="M3 14c2-2 4-2 6 0s4 2 6 0 4-2 6 0"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="font-script text-2xl lg:text-[1.7rem] text-[#0B2F6B] leading-none pt-1">
            Agua Flow Mobile
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#1E7BE0] bg-[#EAF3FF]"
                    : "text-[#0B2F6B] hover:text-[#1E7BE0] hover:bg-[#EAF3FF]/60"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="navbar-call-link"
            className="flex items-center gap-2 text-sm font-medium text-[#0B2F6B] hover:text-[#1E7BE0]"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-cta"
            className="btn-book text-sm"
          >
            Book Now
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-md text-[#0B2F6B]"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden border-t border-[#DCE7F5] bg-white"
        >
          <div className="aqua-container py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl font-medium ${
                    isActive
                      ? "bg-[#EAF3FF] text-[#1E7BE0]"
                      : "text-[#0B2F6B]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-[#DCE7F5]">
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-call-link"
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-[#0B2F6B] font-medium bg-[#EAF3FF]"
              >
                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
              </a>
              <a
                href={BUSINESS.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-cta"
                className="btn-book justify-center"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
